import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useOpenApprovals } from "@api/hooks/useApprovalRequests";
import { DocumentsPager } from "@components/Review/DocumentsPager";
import { SearchableSelect } from "@components/common/SearchableSelect";
import { TableViewControls } from "@components/common/TableViewControls";
import { TableViewLayout } from "@components/common/TableViewLayout";
import { tableHeaderCellClass, tableHeaderRowClass } from "@components/common/tableViewStyles";
import type { OpenApprovalRequest } from "@schemas/api";
import { formatDateTime } from '@utils/formatters';

const labels = {
  invoice: "Invoice",
  receipt: "Receipt",
  purchase_order: "Purchase order",
  ap_posting: "AP posting",
};
const actionLabel = (action: string | null) =>
  action ? action.replace(/_/g, " ") : "Review";

export function OpenApprovalList({
  recordType,
}: {
  recordType?: OpenApprovalRequest["record_type"];
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState<"requested_at" | "type" | "request" | "requested_by" | "awaiting" | "status">("requested_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const query = useOpenApprovals((page - 1) * pageSize, pageSize, recordType);
  const approvals = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLocaleLowerCase();
    const valueFor = (request: OpenApprovalRequest) => {
      if (sortBy === "type") return labels[request.record_type];
      if (sortBy === "request") return request.title;
      if (sortBy === "requested_by") return request.requested_by ?? "";
      if (sortBy === "awaiting") return request.approvers.join(" ");
      if (sortBy === "status") return request.status;
      return new Date(request.requested_at).getTime();
    };
    return [...(query.data?.items ?? [])]
      .filter((request) => !statusFilter || request.status === statusFilter)
      .filter((request) => !normalizedSearch || [
        labels[request.record_type],
        request.title,
        request.requested_by,
        request.approvers.join(" "),
        request.status,
      ].filter(Boolean).join(" ").toLocaleLowerCase().includes(normalizedSearch))
      .sort((left, right) => {
        const leftValue = valueFor(left);
        const rightValue = valueFor(right);
        const comparison = typeof leftValue === "string"
          ? leftValue.localeCompare(String(rightValue))
          : Number(leftValue) - Number(rightValue);
        return sortOrder === "asc" ? comparison : -comparison;
      });
  }, [query.data?.items, searchTerm, sortBy, sortOrder, statusFilter]);
  return (
    <section aria-label="Open approval requests" className="space-y-4">
      <TableViewLayout
        title="Approval requests"
        summary={query.data?.total ? `Showing ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, query.data.total)} of ${query.data.total}` : "Showing 0-0 of 0"}
      >
      <div className="border-b border-border-subtle px-5 py-3 dark:border-border-subtle-dark">
      <TableViewControls
        activeFilterCount={Number(Boolean(statusFilter))}
        onClearFilters={() => setStatusFilter("")}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchLabel="Search approval requests"
        searchPlaceholder="Search request, requester, or approver…"
        filterContent={
          <div>
            <label className="mb-2 block text-sm font-medium text-content-secondary">Status</label>
            <SearchableSelect
              aria-label="Approval request status"
              options={[
                { id: "", label: "All statuses" },
                ...Array.from(new Set((query.data?.items ?? []).map((request) => request.status))).map((status) => ({ id: status, label: status.replace(/_/g, " ") })),
              ]}
              value={statusFilter}
              onChange={setStatusFilter}
              clearable={false}
            />
          </div>
        }
        sortContent={
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-content-secondary">Sort by</label>
              <SearchableSelect
                aria-label="Sort approval requests by"
                options={[
                  { id: "requested_at", label: "Opened" },
                  { id: "type", label: "Type" },
                  { id: "request", label: "Request" },
                  { id: "requested_by", label: "Requested by" },
                  { id: "awaiting", label: "Awaiting" },
                  { id: "status", label: "Status" },
                ]}
                value={sortBy}
                onChange={(value) => setSortBy(value as typeof sortBy)}
                clearable={false}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-content-secondary">Direction</label>
              <SearchableSelect
                aria-label="Approval request sort direction"
                options={[{ id: "asc", label: "Ascending" }, { id: "desc", label: "Descending" }]}
                value={sortOrder}
                onChange={(value) => setSortOrder(value as typeof sortOrder)}
                clearable={false}
              />
            </div>
          </div>
        }
      />
      </div>
      {query.isLoading ? (
        <p role="status">Loading open approval requests…</p>
      ) : query.error ? (
        <p role="alert">Could not load approval requests. Please retry.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table
              className="min-w-full text-left text-sm"
              aria-label="Open approval requests"
            >
              <thead className={tableHeaderRowClass}>
                <tr>
                  {[
                    "Type",
                    "Request",
                    "Requested by",
                    "Awaiting",
                    "Status",
                    "Opened",
                    "Review",
                  ].map((label) => (
                    <th
                      key={label}
                      scope="col"
                      className={`${tableHeaderCellClass} text-left`}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border-default">
                {approvals.map((request) => (
                  <tr key={request.id}>
                    <td className="whitespace-nowrap px-4 py-4">
                      {labels[request.record_type]}
                    </td>
                    <td className="px-4 py-4">
                      <span className="block font-medium text-content-primary">
                        {request.title}
                      </span>
                      <span className="block text-xs capitalize text-content-secondary">
                        {actionLabel(request.action_type)}
                      </span>
                    </td>
                    <td className="px-4 py-4">{request.requested_by || "—"}</td>
                    <td className="px-4 py-4">
                      <span className="block">
                        {request.approvers.join(", ") || "Approver assignment"}
                      </span>
                      <span className="text-xs text-content-secondary">
                        Step {request.current_step}
                        {request.total_steps
                          ? ` of ${request.total_steps}`
                          : ""}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="rounded-full bg-amber-50 px-2 py-1 text-xs capitalize text-amber-900">
                        {request.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4">
                      <time dateTime={request.requested_at}>
                        {formatDateTime(request.requested_at)}
                      </time>
                    </td>
                    <td className="px-4 py-4">
                      {request.review_url ? (
                        <Link
                          to={request.review_url}
                          className="font-medium text-primary-600 underline"
                          aria-label={`Review ${labels[request.record_type].toLowerCase()}: ${request.title}`}
                        >
                          Review
                        </Link>
                      ) : (
                        <span className="text-content-secondary">
                          Unavailable
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!approvals.length && (
              <p
                role="status"
                className="p-8 text-center text-content-secondary"
              >
                No open approval requests.
              </p>
            )}
          </div>
          <DocumentsPager
            total={query.data?.total ?? 0}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            idPrefix="open-approvals"
          />
        </>
      )}
      </TableViewLayout>
    </section>
  );
}
